<script lang="ts">
import type { SelectFieldOption } from '#layers/block-engine/public';
import type { DisplayBlockProps } from '#layers/blocks/types/display-block';
import type { BadgeProps } from '#layers/ui/public';
import { defineBlock, useSelectOptions } from '#layers/block-engine/public';
import { RoundedVariant } from '#layers/ui/public';

type AcceptableValue = string | number | bigint | null;
interface DisplayLabelItem {
	value: string;
	badgeProps: BadgeProps;
}

export interface DisplayLabelsProps extends DisplayBlockProps<AcceptableValue | AcceptableValue[]> {
	options?: SelectFieldOption[];
	/**
	 * Convert the label to title case automatically
	 *
	 * @default true
	 */
	convertToTitleCase?: boolean;

	/**
	 * The display style of the label
	 *
	 * @default 'dot-with-label'
	 */
	displayStyle?: 'dot-only' | 'dot-with-label' | 'label-only';
	/**
	 * Maximum number of labels to show before showing "+X more"
	 *
	 * @default 4
	 */
	limit?: number;

	/**
	 * Variants that work for both Badge and Button
	 */
	variant?: 'solid' | 'outline' | 'subtle' | 'soft';
	rounded?: RoundedVariant;
}

const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_labels.name',
	description: '$t:blocks.display_labels.description',
	icon: 'lucide:square-dot',
	category: 'display',
	options: [
		{ name: 'displayStyle', type: 'enum', values: ['dot-only', 'dot-with-label', 'label-only'] },
	],
	hidden: true,

	fieldBinding: {
		supportedTypes: ['text', 'number', 'select'],
		defaultProps: ({ fieldTypeId, typeOptions }) => {
			if (fieldTypeId === 'select') {
				return {
					options: typeOptions?.options,
					variant: 'outline',
					rounded: RoundedVariant.Full,
				};
			}
		},
	},
}</script>

<script setup lang="ts">
import { formatTitle } from '@directus/format-title';
import { createReusableTemplate } from '@vueuse/core';
import { computed } from 'vue';
import { ensureArray, isNil } from '#layers/base/public';
import { useDisplayTemplateNode } from './use-display-template-node';

const props = withDefaults(defineProps<DisplayLabelsProps>(), {
	convertToTitleCase: true,
	displayStyle: 'dot-with-label',
	rounded: RoundedVariant.Full,
	limit: 4,
});

;

const { normalizedOptions: options } = useSelectOptions(() => props.options);

const items = computed<DisplayLabelItem[]>(() => ensureArray(props.value)
	.filter((value) => !isNil(value))
	.map((value) => {
		const stringValue = String(value);
		const option = options.value.find((option) => option.value === value);
		const hasColor = !isNil(option?.color);
		const badgeProps = {
			label: props.convertToTitleCase ? formatTitle(stringValue) : stringValue,
			...option,
			color: hasColor ? 'custom' : 'neutral',
			variant: (props.variant ?? option?.color) ? 'subtle' : 'outline',
			customColor: option?.color,
		} satisfies BadgeProps;

		return {
			value: stringValue,
			badgeProps,
		};
	}),
);

const regularItems = computed(() => items.value.length <= props.limit ? items.value : items.value.slice(0, props.limit - 1));
const overflowItems = computed(() => items.value.length > props.limit ? items.value.slice(props.limit - 1) : []);
const overflowItemsWithColor = computed(() => overflowItems.value.filter((item) => item.badgeProps.customColor));
const { isNestedDisplayTemplateNode, displayTemplateNodeElement } = useDisplayTemplateNode(() => props.root);
const rootClasses = computed(() =>
	isNestedDisplayTemplateNode.value
		? 'inline-flex h-lh max-w-full min-w-0 items-center gap-1'
		: 'flex max-w-full min-w-0 items-center gap-1');

const [DefineBadge, ReuseBadge] = createReusableTemplate<{ item: DisplayLabelItem; displayStyle: DisplayLabelsProps['displayStyle']; fitClass?: string }>({ inheritAttrs: false });

const rowFitClasses = computed(() => isNestedDisplayTemplateNode.value ? 'h-full max-h-6 py-0' : '');

/** `DButton` still takes a boolean, so the overflow trigger has to translate the badge shape. */
const isOverflowTriggerRounded = computed(() => props.rounded === RoundedVariant.Full);
</script>

<template>
	<component :is="displayTemplateNodeElement" :class="rootClasses">
		<DefineBadge v-slot="{ item, displayStyle: itemDisplayStyle, fitClass }">
			<DColorDot v-if="itemDisplayStyle === 'dot-only' && item.badgeProps.customColor" :color="item.badgeProps.customColor" size="xs" />

			<DBadge
				v-else
				v-bind="item.badgeProps"
				class="
					max-w-full min-w-0 gap-1.5 overflow-hidden whitespace-nowrap
					**:data-[slot=label]:min-w-0 **:data-[slot=label]:truncate
				"
				:class="fitClass"
				:rounded
			>
				<template v-if="itemDisplayStyle === 'dot-with-label' && (item.badgeProps.icon || item.badgeProps.customColor)" #leading>
					<DIconFrame
						v-if="item.badgeProps.icon"
						:color="item.badgeProps.customColor"
						:name="item.badgeProps.icon"
						class="-my-0.5 -ms-1 -me-1 size-5"
						variant="link"
					/>
					<DColorDot v-else :color="item.badgeProps.customColor" size="xs" />
				</template>
			</DBadge>
		</DefineBadge>

		<ReuseBadge v-for="item in regularItems" :key="item.value" :item :display-style :fit-class="rowFitClasses" />
		<DPopover
			v-if="overflowItems.length > 0"
			mode="hover"
			:ui="{ content: 'flex max-w-64 flex-wrap items-start gap-1 p-2' }"
		>
			<DButton
				color="neutral"
				size="xs"
				class="font-semibold"
				:class="rowFitClasses"
				:variant="variant ?? 'outline'"
				:rounded="isOverflowTriggerRounded"
				:label="$t('blocks.display_labels.n_more', overflowItems.length)"
			>
				<template v-if="displayStyle !== 'label-only' && overflowItemsWithColor.length > 0" #leading>
					<span
						v-for="(item, index) in overflowItemsWithColor"
						:key="item.value"
						class="size-2 shrink-0 rounded-full outline-[0.5px] outline-(--ui-bg)"
						:class="index > 0 ? `-ml-2` : ''"
						aria-hidden="true"
						:style="{ backgroundColor: item.badgeProps.customColor }"
					/>
				</template>
			</DButton>

			<template #content>
				<ReuseBadge v-for="item in overflowItems" :key="item.value" :item display-style="dot-with-label" />
			</template>
		</DPopover>
	</component>
</template>
